import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { compose } from 'redux';
import { connect as connectRedux } from 'react-redux';
import { PropTypes } from 'prop-types';

import withLayout from '../../../core/components/with-layout';

import {
  Container,
  Loader,
} from './styles';
import { LIVESTREAM_TEST_SESSION } from './graphql';

import useLivestreamWebSocket from '../../../core/components/with-socket-connection';

import TestSessionNotStarted from './test-session-not-started';
import FullRoom from './full-room';
import VideoIvsPlayer from './video-ivs-player';
import { signInWithLivestream } from '../../../core/actions';
import Graphql from '../../../core/services/graphql';
import ErrorWebsocket from './error-websocket';
import EndStream from './end-stream';

const ModeratedLivestream = (props) => {
  const {
    onSignInWithLivestream,
    tokenFromCompany,
  } = props;

  const [ivsUrl, setIvsUrl] = useState(null);
  const [testSession, setTestSession] = useState(null);
  const [errorEvent, setErrorEvent] = useState(null);
  const [roomIsFull, setRoomIsFull] = useState(false);
  const [streamHasEnded, setStreamHasEnded] = useState(false);

  const { testSessionId } = useParams();

  const {
    onEvent,
    offEvent,
    isConnected,
    error,
  } = useLivestreamWebSocket(testSessionId);

  useEffect(() => {
    const signInAsync = async () => {
      const query = Graphql;

      if (!tokenFromCompany) {
        const token = await onSignInWithLivestream(testSessionId);
        query.setToken(token);
        query.setScope('livestream');
      } else {
        query.setToken(tokenFromCompany);
      }

      const { data } = await query.query({
        query: LIVESTREAM_TEST_SESSION,
        variables: {
          id: testSessionId,
        },
      });
      setTestSession(data.livestreamTestSession);
    };
    signInAsync();
  }, [testSessionId, onSignInWithLivestream, tokenFromCompany]);

  useEffect(() => {
    const streamStarted = ({ url }) => {
      setIvsUrl(url);
      setStreamHasEnded(false);
    };

    const eventError = (err) => {
      setIvsUrl(null);
      setErrorEvent(err.message);
    };

    const roomIsFullHandler = () => {
      setIvsUrl(null);
      setRoomIsFull(true);
    };

    const roomSlotAvailableHandler = ({ url }) => {
      setIvsUrl(url);
      setRoomIsFull(false);
    };

    const streamEndHandler = () => {
      setIvsUrl(null);
      setStreamHasEnded(true);
    };

    onEvent('streamStart', streamStarted);
    onEvent('streamEnd', streamEndHandler);
    onEvent('roomIsFull', roomIsFullHandler);
    onEvent('roomSlotAvailable', roomSlotAvailableHandler);
    onEvent('error', eventError);
  }, [offEvent, onEvent, testSession]);

  const hasErrors = errorEvent || error;

  return (
    <Container>
      {/* Show livestream screen */}
      {
        !hasErrors && isConnected && testSession && !roomIsFull && ivsUrl && !streamHasEnded && (
          <VideoIvsPlayer streamUrl={ivsUrl} />
        )
      }

      {/* Show waiting room screen */}
      {
        !hasErrors && isConnected && testSession && !roomIsFull && !ivsUrl && !streamHasEnded && (
          <TestSessionNotStarted testSession={testSession} />
        )
      }

      {/* Show full room screen */}
      {
        !hasErrors && isConnected && testSession && roomIsFull && !streamHasEnded && (
          <FullRoom />
        )
      }

      {/* Show end stream screen */}
      {
        !hasErrors && isConnected && testSession && streamHasEnded && (
          <EndStream />
        )
      }

      {/* Show loader */}
      {
        !hasErrors && !testSession && (
          <Loader data-loader="moderated-livestream-loader" />
        )
      }

      {/* Show error screen */}
      {
        hasErrors && (
          <ErrorWebsocket />
        )
      }
    </Container>
  );
};

ModeratedLivestream.defaultProps = {
  tokenFromCompany: null,
};

ModeratedLivestream.propTypes = {
  onSignInWithLivestream: PropTypes.func.isRequired,
  tokenFromCompany: PropTypes.string,
};

const mapStateToProps = (state) => ({
  tokenFromCompany: state.authentication && state.authentication.token,
});

const mapDispatchToProps = {
  onSignInWithLivestream: signInWithLivestream,
};

export default compose(
  connectRedux(mapStateToProps, mapDispatchToProps),
  withLayout(),
)(ModeratedLivestream);
